import { type BreadcrumbItem } from "@/app/(site)/_components/Molecules/Breadcrumbs"
import {
    type CheckoutStep,
    type GlobalState,
    type Size,
    type Variant
} from "@/app/(site)/contexts/ecommerce/types"
import { type ColorSwatch } from "@/app/(site)/contexts/Global/types"
import { type RequiredTokenAndSelectionMetadata } from "@/types/cookie-types"
import { create } from "zustand"

interface GlobalActions {
    // Cart
    toggleCartMenu: () => void
    closeCartMenu: () => void

    // Mobile menu
    setShowMobileMenu: (show: boolean) => void
    showMobileMenu: boolean

    // Cookie bar
    toggleCookieBar: () => void
    closeCookieBar: () => void

    // Market menu
    toggleMarketMenu: () => void
    closeMarketMenu: () => void

    // Nav menu
    toggleNavMenu: () => void
    setShowNavMenu: (show: boolean) => void
    closeNavMenu: () => void

    // Search menu
    toggleSearchMenu: () => void
    closeSearchMenu: () => void

    // Store message
    toggleStoreMessage: () => void
    closeStoreMessage: () => void

    // Top notification bar
    closeTopNotificationBar: () => void

    // Sizes
    setSizes: (sizes: Size[]) => void
    setSelectedSizeName: (setSelectedSizeName: string) => void

    // Color Swatches
    setColorSwatches: (colorSwatches: ColorSwatch[]) => void
    setSelectedVariant: (selectedVariant: Variant) => void

    // Variant category name
    setVariantCategoryName: (variantCategoryName: BreadcrumbItem[]) => void

    // Notify Me modal
    toggleNotifyModal: (isOpen: boolean) => void

    // Search Modal
    toggleSearchModal: (isOpen: boolean) => void

    // Country picker
    setShowCountryPicker: (show: boolean) => void

    /**
     * Centra Session
     *
     * Accepts `null` to clear the slice (used after order completion in Phase 5b
     * once the active-shape cookie is stripped to the preference shape, and after
     * `useSWRBasketSessionState` revalidates and finds no active basket).
     */
    setBasketSession: (basketSession: RequiredTokenAndSelectionMetadata | null) => void

    // Wrong site modal
    toggleWrongSiteModal: (isOpen: boolean) => void

    // Checkout step management
    setCheckoutCurrentStep: (step: CheckoutStep) => void
    removeAllCheckoutSteps: () => void

    //Price Histiory Modal
    togglePriceHistoryModal: () => void
    closePriceHistoryModal: () => void

    //Product Attribute Modal
    toggleProductAttributeModal: () => void
    closeProductAttributeModal: () => void

    //Taxes Information Modal
    toggleTaxesInformationModal: () => void
}

const useEcommerceStore = create<GlobalState & GlobalActions>(set => ({
    // Initial states
    isLoadingImages: false,
    isCartMenuOpen: false,
    isCookieBarOpen: false,
    isMarketMenuOpen: false,
    isWrongSiteModalOpen: false,
    isNavMenuOpen: false,
    isSearchMenuOpen: false,
    isStoreMessageOpen: false,
    isNotifyMeModalOpen: false,
    isSearchModalOpen: false,
    isPriceHistoryModalOpen: false,
    isProductAttributeModalOpen: false,
    isTopNotificationBarOpen: true,
    isTaxesInformationModalOpen: false,
    showCountryPicker: false,
    sizes: [],
    selectedSizeName: "",
    colorSwatches: [],
    selectedVariant: {} as Variant,
    variantCategoryName: [],
    showMobileMenu: false,
    basketSession: null,
    // Checkout step management
    checkoutCurrentStep: 1,
    checkoutLoadedSteps: [1],

    toggleNavMenu: () => set(state => ({ isNavMenuOpen: !state.isNavMenuOpen })),
    setShowNavMenu: (show: boolean) => set({ isNavMenuOpen: show }),

    // Actions
    toggleCartMenu: () => set(state => ({ isCartMenuOpen: !state.isCartMenuOpen })),
    closeCartMenu: () => set({ isCartMenuOpen: false }),

    // Cookie bar
    toggleCookieBar: () => set(state => ({ isCookieBarOpen: !state.isCookieBarOpen })),
    closeCookieBar: () => set({ isCookieBarOpen: false }),

    // Market menu
    toggleMarketMenu: () => set(state => ({ isMarketMenuOpen: !state.isMarketMenuOpen })),
    closeMarketMenu: () => set({ isMarketMenuOpen: false }),

    // Nav menu
    closeNavMenu: () => set({ isNavMenuOpen: false }),

    // Mobile menu
    setShowMobileMenu: (show: boolean) => set({ showMobileMenu: show }),

    // Search menu
    toggleSearchMenu: () => set(state => ({ isSearchMenuOpen: !state.isSearchMenuOpen })),
    closeSearchMenu: () => set({ isSearchMenuOpen: false }),

    // Store message
    toggleStoreMessage: () =>
        set(state => ({ isStoreMessageOpen: !state.isStoreMessageOpen })),
    closeStoreMessage: () => set({ isStoreMessageOpen: false }),

    // Top notification bar
    closeTopNotificationBar: () => set({ isTopNotificationBarOpen: false }),

    // Country picker
    // Opening the country picker always closes the mobile menu so the two
    // overlays can never fight for the viewport or focus at the same time.
    setShowCountryPicker: show =>
        set(
            show
                ? { showCountryPicker: true, showMobileMenu: false }
                : { showCountryPicker: false }
        ),

    // Sizes and color swatches
    setSizes: sizes => set({ sizes }),
    setColorSwatches: colorSwatches => set({ colorSwatches }),
    setSelectedSizeName: selectedSizeName => set({ selectedSizeName }),

    setVariantCategoryName: variantCategoryName => set({ variantCategoryName }),

    setSelectedVariant: selectedVariant => set({ selectedVariant }),
    toggleNotifyModal: isOpen => set({ isNotifyMeModalOpen: isOpen }),

    toggleSearchModal: isOpen => set({ isSearchModalOpen: isOpen }),

    // Centra Session
    setBasketSession: basketSession => set({ basketSession }),

    // Wrong site modal
    toggleWrongSiteModal: isOpen => set({ isWrongSiteModalOpen: isOpen }),

    // Checkout step management
    setCheckoutCurrentStep: (step: CheckoutStep) => {
        // Validate step is within allowed range
        if (step < 1 || step > 3) {
            console.warn(`Invalid checkout step: ${step}. Must be between 1-3.`)
            return
        }

        set(state => ({
            checkoutCurrentStep: step,
            checkoutLoadedSteps: state.checkoutLoadedSteps.includes(step)
                ? state.checkoutLoadedSteps
                : [...state.checkoutLoadedSteps, step]
        }))
    },

    removeAllCheckoutSteps: () =>
        set(() => ({
            checkoutCurrentStep: 1,
            checkoutLoadedSteps: [1]
        })),

    //Price History Modal
    togglePriceHistoryModal: () =>
        set(state => ({ isPriceHistoryModalOpen: !state.isPriceHistoryModalOpen })),

    closePriceHistoryModal: () => set({ isPriceHistoryModalOpen: false }),

    //Product Attribute Modal
    toggleProductAttributeModal: () =>
        set(state => ({
            isProductAttributeModalOpen: !state.isProductAttributeModalOpen
        })),

    closeProductAttributeModal: () => set({ isProductAttributeModalOpen: false }),

    //Taxes Information Modal
    toggleTaxesInformationModal: () =>
        set(state => ({
            isTaxesInformationModalOpen: !state.isTaxesInformationModalOpen
        }))
}))

export default useEcommerceStore
